import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { use, useEffect, useState } from 'react'
import {
  AddSkywardFeeItemRequest,
  AddSkywardFoodItemRequest,
  addSkywardFeeItem,
  addSkywardFoodItem,
  createCart,
} from '../api/commerce-api'
import {
  AddSkywardLowBalancePropsLegacy,
  cancelSkywardLowBalanceAlertLegacy,
  cancelSkywardRecurringFoodPaymentLegacy,
  createSkywardLowBalanceAlertLegacy,
  getSkywardLowBalanceAlertLegacy,
  getSkywardRecurringFoodPaymentsLegacy,
} from '../api/legacy/skyward-legacy-api'
import { fetchSkywardAccounts, skywardConnect } from '../api/skyward-api'
import useSession from '../app/(public)/auth/_components/hooks/use-session'
import { logger } from '../lib/logger'
import { useCartItems, useCartStore, useCartStoreActions } from '../stores/cart-store'
import { useSkywardStore } from '../stores/skyward-store'
import { AddCartItem, ItemTypes, RecurringFrequency } from '../types/commerce-types'
import { SkywardConnection, SkywardItem } from '../types/skyward-types'
import { useCart } from './use-cart'

export const useSkyward = () => {
  const {
    setAccounts,
    setConnection,
    connectInfo,
    setError,
    loadingStates,
    setLoadingState,
    setSite,
    setSessionId,
    setSelectedAccount,
    clearSelectedAccount,
  } = useSkywardStore()
  const selectedAccount = useSkywardStore((state) => state.selectedAccount)
  const sessionId = useSkywardStore((state) => state.sessionId)
  const site = useSkywardStore((state) => state.site)
  const { addItem, removeItem, cartKey, cart } = useCart()
  const resetCart = useCartStore((x) => x.reset)
  const queryClient = useQueryClient()
  const [skywardItems, setSkywardItems] = useState<SkywardItem[]>([])
  useEffect(() => {
    if (selectedAccount?.existingFees.length || 0 > 0) {
      logger.info('Selected account has existing fees', {
        account: selectedAccount!.nameId,
        fees: selectedAccount!.existingFees.length,
      })
      const items =
        selectedAccount?.existingFees.map(
          (x, i) =>
            ({
              ...x,
              amount: x.amount / 100,
              due: x.due / 100,
              paid: x.paid / 100,
            } as SkywardItem),
        ) ?? []

      items.forEach((item) => {
        const cartItem = cart?.items.find((x) => x.meta?.skyward?.feeId == item.feeId)
        if (cartItem != null) {
          item.inCart = true
          item.cartItemId = cartItem.id
          item.recurringFrequency = cartItem.meta?.recurring?.frequency
          item.recurringAmount = cartItem.meta?.recurring?.amount
          item.isRecurring = cartItem.meta?.recurring?.amount ?? 0 > 0 ? true : false
        } else {
          item.inCart = false
          item.cartItemId = undefined
          item.recurringFrequency = undefined
          item.recurringAmount = undefined
        }
        if (site != undefined) {
          item.allowPartialPayment = site.features!.some((x) => x == 'SKYWARD_PARTIAL_PAYMENTS') || false
          item.allowRecurring = site.features!.some((x) => x == 'SKYWARD_RECURRING_PAYMENTS') || false
        }
      })

      setSkywardItems(items)
    } else {
      setSkywardItems([])
    }
  }, [cart, selectedAccount])
  useEffect(() => {
    logger.info('Skyward items updated', { skywardItems })
  }, [skywardItems])

  useEffect(() => {
    logger.info('cartItems items updated', { cart })
  }, [cart])
  const { mutateAsync: connect, error: connectError } = useMutation({
    mutationKey: ['skyward-connect'],
    mutationFn: async (connectInfo: SkywardConnection) => {
      try {
        logger.info('Connecting to Skyward with connection info', { connectInfo })
        setLoadingState('connect', true)
        const result = await skywardConnect(connectInfo)
        setConnection(connectInfo)
        setSite(result.site)
        setSessionId(result.sessionId || '')
        setAccounts((result.accounts as any) || [])
        logger.info('Skyward connection successful', { result })
        resetCart()
        queryClient.invalidateQueries({ queryKey: ['skyward-accounts'] })
        return result
      } catch (error) {
        logger.error('Error connecting to Skyward', { error })
        setError('Failed to connect to Skyward')
        throw error
      } finally {
        setLoadingState('connect', false)
      }
    },
  })
  const {
    refetch,
    error: accountsError,
    isError: hasAccountsError,
  } = useQuery({
    queryKey: ['skyward-accounts'],
    queryFn: async () => {
      setLoadingState('accounts', true)
      try {
        const results = await fetchSkywardAccounts()

        setAccounts(results || [])
        return results
      } catch (error) {
        logger.error('Error fetching Skyward accounts', { error })
        setError('Failed to fetch Skyward accounts')
        throw error
      }
    },
    enabled: !!sessionId,
  })
  /**
   * Add a Skyward fee payment to the cart using v2 API endpoint.
   * POST /api/v2/cart/skyward-fees
   */
  const addSkywardFeePayment = async (fee: SkywardItem) => {
    if (!selectedAccount) {
      throw new Error('No Skyward account selected')
    }
    if (!fee || !fee.feeId || !fee.amount) {
      throw new Error('Invalid fee data')
    }

    // Get device session ID for auto-create cart capability
    // Related: PL-500 - Session-based cart persistence
    const deviceSessionId = useCartStore.getState().getSessionId()

    // Auto-create cart if cartKey is missing
    // Related: PL-509 - Fix for users without existing cart
    let effectiveCartKey = cartKey
    if (!effectiveCartKey) {
      logger.info('No cart key found, creating new cart via v2 API for fee payment', { sessionId: deviceSessionId })
      try {
        const cartResponse = await createCart({ sessionId: deviceSessionId })
        effectiveCartKey = cartResponse.cartKey
        // Update the cart store with the new key
        useCartStore.getState().setCartKey(cartResponse.cartKey)
        logger.info('Cart created successfully for fee payment', {
          cartKey: cartResponse.cartKey,
          cartId: cartResponse.cartId,
        })
      } catch (cartError) {
        logger.error('Failed to create cart for fee payment', { error: cartError })
        throw new Error('Failed to create cart for fee payment')
      }
    }

    const isRecurring =
      fee.recurringFrequency === 'weekly' ||
      fee.recurringFrequency === 'bi-weekly' ||
      fee.recurringFrequency === 'monthly' ||
      fee.recurringFrequency === 'low-balance'

    const due = fee.due ?? fee.amount

    // Map recurringFrequency from SkywardItem (string) to the expected union type
    const mapRecurringFrequency = (freq?: string): 'one-time' | 'weekly' | 'bi-weekly' | 'monthly' | undefined => {
      if (!freq || freq === 'one-time') return 'one-time'
      if (freq === 'weekly' || freq === 'bi-weekly' || freq === 'monthly') return freq
      return undefined
    }

    // PL-509 FIX: Safely parse studentId to ensure we never send NaN
    // The fee.nameId can be either number (SkywardFee) or string (SkywardPayment)
    // We must ensure a valid positive number is sent to the backend
    let studentId: number
    if (typeof fee.nameId === 'string') {
      studentId = parseInt(fee.nameId, 10)
    } else if (typeof fee.nameId === 'number') {
      studentId = fee.nameId
    } else {
      studentId = 0
    }

    // Validate studentId - if invalid, throw early with a clear error
    if (isNaN(studentId) || studentId <= 0) {
      logger.error('Invalid studentId detected', {
        feeNameId: fee.nameId,
        parsedStudentId: studentId,
        feeId: fee.feeId,
      })
      throw new Error(`Invalid student ID: ${fee.nameId}. Please select a valid account.`)
    }

    const request: AddSkywardFeeItemRequest = {
      cartKey: String(effectiveCartKey),
      sessionId: deviceSessionId,
      feeId: String(fee.feeId),
      feeBcId: fee.fmBcId ? String(fee.fmBcId) : undefined,
      name: fee.name,
      description: fee.description,
      amount: due > fee.amount ? fee.amount : due,
      studentId,
      studentName: `${selectedAccount.firstName} ${selectedAccount.lastName}`,
      schoolId: parseInt(selectedAccount.tenantId ?? '0'),
      recurringFrequency: mapRecurringFrequency(fee.recurringFrequency),
      firstRecurDate: isRecurring ? moment().toISOString() : undefined,
    }

    logger.info('Adding Skyward fee to cart via v2 API', { feeId: fee.feeId, amount: request.amount })
    const result = await addSkywardFeeItem(request)
    queryClient.invalidateQueries({ queryKey: ['cart'] })
    return result
  }

  /**
   * Add a Skyward food service payment to the cart using v2 API endpoint.
   * POST /api/v2/cart/skyward-food
   *
   * Related: PL-500 - Auto-creates cart if none exists in the session.
   */
  const addSkywardFoodPayment = async (payment: SkywardItem) => {
    if (!selectedAccount) {
      throw new Error('No Skyward account selected')
    }
    if (!payment.amount || payment.amount <= 0) {
      throw new Error('Amount must be greater than 0')
    }

    // Get device session ID for cart persistence
    // Related: PL-500 - Session-based cart persistence
    const deviceSessionId = useCartStore.getState().getSessionId()

    // Auto-create cart if cartKey is missing
    // Related: PL-500 - Fix for manual browser sessions without existing cart
    let effectiveCartKey = cartKey
    if (!effectiveCartKey) {
      logger.info('No cart key found, creating new cart via v2 API', { sessionId: deviceSessionId })
      try {
        const cartResponse = await createCart({ sessionId: deviceSessionId })
        effectiveCartKey = cartResponse.cartKey
        // Update the cart store with the new key
        useCartStore.getState().setCartKey(cartResponse.cartKey)
        logger.info('Cart created successfully', {
          cartKey: cartResponse.cartKey,
          cartId: cartResponse.cartId,
        })
      } catch (cartError) {
        logger.error('Failed to create cart', { error: cartError })
        throw new Error('Failed to create cart for food payment')
      }
    }

    const isRecurring =
      payment.recurringFrequency === 'weekly' ||
      payment.recurringFrequency === 'bi-weekly' ||
      payment.recurringFrequency === 'monthly' ||
      payment.recurringFrequency === 'low-balance'

    // Map recurringFrequency from SkywardItem (string) to the expected union type for food items
    const mapFoodRecurringFrequency = (
      freq?: string,
    ): 'one-time' | 'weekly' | 'bi-weekly' | 'monthly' | 'low-balance' | undefined => {
      if (!freq || freq === 'one-time') return 'one-time'
      if (freq === 'weekly' || freq === 'bi-weekly' || freq === 'monthly' || freq === 'low-balance') return freq
      return 'one-time'
    }

    const request: AddSkywardFoodItemRequest = {
      cartKey: String(effectiveCartKey),
      sessionId: deviceSessionId,
      amount: payment.amount,
      studentId: typeof payment.nameId === 'string' ? parseInt(payment.nameId, 10) : payment.nameId,
      studentName: `${selectedAccount.firstName} ${selectedAccount.lastName}`,
      schoolId: parseInt(selectedAccount.tenantId ?? '0'),
      recurringFrequency: mapFoodRecurringFrequency(payment.recurringFrequency),
      firstRecurDate: isRecurring ? moment().toISOString() : undefined,
      lowBalanceThreshold: payment.recurringFrequency === 'low-balance' ? payment.threshold : undefined,
    }

    logger.info('Adding Skyward food payment to cart via v2 API', {
      amount: request.amount,
      studentId: request.studentId,
    })
    const result = await addSkywardFoodItem(request)
    queryClient.invalidateQueries({ queryKey: ['cart'] })
    return result
  }

  const toAddCartItem = (payment: SkywardItem): AddCartItem => {
    const isRecurring =
      payment.recurringFrequency === 'weekly' ||
      payment.recurringFrequency === 'bi-weekly' ||
      payment.recurringFrequency === 'monthly' ||
      payment.recurringFrequency === 'low-balance'
    const due = payment.due ?? payment.amount
    return {
      integrationId: 1,
      feeId: payment.feeId,
      fee_bcId: payment.fmBcId,
      name: payment.name,
      description: payment.description,
      studentId: payment.nameId,
      studentName: selectedAccount?.firstName + ' ' + selectedAccount?.lastName,
      schoolId: parseInt(selectedAccount?.tenantId ?? '0'),
      itemType: 1,
      cartKey: payment.cartKey,
      amount: due > payment.amount ? payment.amount : payment.due,
      recurringFrequency: payment.recurringFrequency,
      firstRecurDate: isRecurring ? moment().format('MM/DD/YY') : '',
      lowBalanceRecur: payment.threshold && payment.recurringFrequency == 'low-balance' ? payment.threshold : undefined,
    } as AddCartItem
  }

  return {
    removeItem,
    skywardItems,
    clearSelectedAccount,
    addSkywardFeePayment,
    addSkywardFoodPayment,
    accounts: useSkywardStore((state) => state.accounts),
    site: useSkywardStore((state) => state.site),
    connect: connect,
    refetchAccounts: refetch,
    error: useSkywardStore((state) => state.error),
    setSelectedAccount: useSkywardStore((state) => state.setSelectedAccount),
    selectedAccount: useSkywardStore((state) => state.selectedAccount),
    loadingStates,
    connectInfo,
    sessionId,
    connectError,
    accountsError,
    hasAccountsError,
    isConnecting: useSkywardStore((state) => state.loadingStates.connect),
  }
}
export const useSkywardLowBalanceAlerts = () => {
  const { setLoadingState } = useSkywardStore()
  const queryClient = useQueryClient()
  const { session } = useSession()
  useEffect(() => {
    logger.info('Skyward low balance alerts hook initialized', { session })
  }, [])
  useEffect(() => {
    logger.info('Skyward low balance alerts hook initialized', { session })
  }, [session])
  const {
    data: lowBalanceAlerts,
    isLoading: isLoadingLowBalanceAlerts,
    error,
  } = useQuery({
    queryKey: ['skyward-low-balance-alerts'],
    queryFn: async () => {
      setLoadingState('low-balance-alerts', true)
      try {
        const result = await getSkywardLowBalanceAlertLegacy()
        return result || []
      } catch (error) {
        logger.error('Error fetching Skyward low balance alerts', { error })
        throw error
      } finally {
        setLoadingState('low-balance-alerts', false)
      }
    },
    enabled: session?.isAuthenticated === true,
  })
  const cancelLowBalanceAlertMutation = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      setLoadingState('cancel-low-balance-alert', true)
      try {
        const result = await cancelSkywardLowBalanceAlertLegacy(id)
        return result
      } catch (error) {
        logger.error('Error canceling Skyward low balance alert', { error })
        throw error
      } finally {
        setLoadingState('cancel-low-balance-alert', false)
        queryClient.invalidateQueries({ queryKey: ['skyward-low-balance-alerts'] })
      }
    },
  })
  const createLowBalanceAlertMutation = useMutation({
    mutationFn: async (alert: AddSkywardLowBalancePropsLegacy) => {
      setLoadingState('create-low-balance-alert', true)
      try {
        const result = await createSkywardLowBalanceAlertLegacy(alert)
        return result
      } catch (error) {
        logger.error('Error creating Skyward low balance alert', { error })
        throw error
      } finally {
        setLoadingState('create-low-balance-alert', false)
        queryClient.invalidateQueries({ queryKey: ['skyward-low-balance-alerts'] })
      }
    },
  })
  return {
    createLowBalanceAlert: createLowBalanceAlertMutation.mutateAsync,
    cancelLowBalanceAlert: cancelLowBalanceAlertMutation.mutateAsync,
    error,
    isLoading: isLoadingLowBalanceAlerts,
    lowBalanceAlerts,
  }
}

export const useSkywardRecurringFoodPayments = ({
  history = false,
  payments = true,
  showFailed = false,
}: {
  history?: boolean
  payments?: boolean
  showFailed?: boolean
}) => {
  const { setLoadingState } = useSkywardStore()

  const queryClient = useQueryClient()
  const { session } = useSession()
  const { data: recurringPayments } = useQuery({
    queryKey: ['skyward-recurring-payments'],
    queryFn: async () => {
      setLoadingState('recurring-payments', true)
      try {
        const result = await getSkywardRecurringFoodPaymentsLegacy(history, payments, showFailed)
        return result
      } finally {
        setLoadingState('recurring-payments', false)
      }
    },
    enabled: session?.isAuthenticated === true,
  })
  const cancelSkywardRecurringPaymentMutation = useMutation({
    mutationFn: async (id: number) => {
      setLoadingState('cancel-recurring-payment', true)
      try {
        const result = await cancelSkywardRecurringFoodPaymentLegacy(id)
        return result
      } catch (error) {
        logger.error('Error canceling Skyward recurring payment', { error })
        throw error
      } finally {
        setLoadingState('cancel-recurring-payment', false)
        queryClient.invalidateQueries({ queryKey: ['skyward-recurring-payments'] })
      }
    },
  })
  return {
    recurringPayments,
    cancelRecurringPayment: cancelSkywardRecurringPaymentMutation.mutateAsync,
  }
}
